import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { OrderCreateRefundModal } from '@/types/modal';
import { useOrder, useOrderRefund, useOrderRefundDetail } from '@/hooks/order';
import { Dropdown, Input } from '@/components/molecules/common/Form';
import { ErrorModal, LoadingModal, StandardModal, SuccessModal } from '@/components/molecules/common/Modal';
import { formatCurrency } from '@/utilities/currency';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: OrderCreateRefundModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { order, onClose } = modal;
  const yesNoOptions = ['Yes', 'No'].map((value) => ({ value, label: value }));
  const boolToYesNo = (value: boolean) => (value ? 'Yes' : 'No');
  const yesNoToBool = (value: string) => value === 'Yes';

  const [hasDiscount, setHasDiscount] = useState(true);
  const [selectedItem, setSelectedItem] = useState('');
  const [memo, setMemo] = useState<string>('');
  const [refundType, setRefundType] = useState<string>('');

  const orderR = useOrder(Number(order.orderId), false);
  const detailR = useOrderRefundDetail(Number(order.orderId));
  const submitR = useOrderRefund();

  useEffect(() => {
    if (detailR.kind === 'Loaded') {
      const refundTypes = detailR.data.types;
      setRefundType(refundTypes[0] ? String(refundTypes[0].id) : '');

      if (detailR.data.products.length === 1) {
        setSelectedItem(String(detailR.data.products[0].id));
      }
    }
  }, [detailR.kind]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      orderR.refetch();
    }
  }, [submitR.kind]);

  if (detailR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.order_create_refund.title')}
        onClose={onClose}
      />
    );
  } else if (detailR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.order_create_refund.title')}
        error={detailR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.order_create_refund.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.order_create_refund.title')}
        onClose={onClose}
      />
    );
  }

  const refundDetail = detailR.data;

  const selectedItemInfo = refundDetail.products.find((info) => String(info.id) === selectedItem);

  let amountToRefund = !selectedItemInfo
    ? 0
    : selectedItemInfo.price -
      (hasDiscount ? selectedItemInfo.discountFeeValue || 0 : 0) +
      (selectedItemInfo.shippingFeeValue || 0) +
      (selectedItemInfo.dutyAndTaxFeeValue || 0) +
      (selectedItemInfo.authFeeValue || 0);
  if (amountToRefund > refundDetail.totalPaidValue) {
    amountToRefund = refundDetail.totalPaidValue;
  }
  amountToRefund = amountToRefund * refundDetail.currencyChange;

  const body = (
    <div className={styles.orderCreateRefund}>
      <div />
      <div className={styles.text1rem}>{`${t('modal.order_create_refund.order')} #${order.orderId}`}</div>
      <div />
      <div className={styles.orderCreateRefundRow}>
        <div className={styles.text0875rem}>{t('modal.order_create_refund.order_value')}</div>
        <div className={styles.text0875rem}>
          {formatCurrency({
            value: refundDetail.totalOrderValue * refundDetail.currencyChange || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div className={styles.orderCreateRefundRow}>
        <div className={styles.text0875rem}>{t('modal.order_create_refund.order_refund_value')}</div>
        <div className={styles.text0875rem}>
          {formatCurrency({
            value: refundDetail.totalRefundedValue * refundDetail.currencyChange || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div className={styles.orderCreateRefundRow}>
        <div className={styles.text0875rem}>{t('modal.order_create_refund.order_paid_value')}</div>
        <div className={styles.text0875rem}>
          {formatCurrency({
            value: refundDetail.totalPaidValue * refundDetail.currencyChange || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div className={styles.orderCreateRefundRow}>
        <div className={clsx([styles.text0875rem, styles.textBold])}>
          {t('modal.order_create_refund.order_remaining_value')}
        </div>
        <div className={clsx([styles.text0875rem, styles.textBold])}>
          {formatCurrency({
            value: refundDetail.remainValue * refundDetail.currencyChange || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div />
      <div />
      <Dropdown
        required={true}
        label={t('modal.order_create_refund.item_id')}
        value={selectedItem}
        onSelect={(v) => {
          setSelectedItem(v);
          setHasDiscount(true);
        }}
        items={refundDetail.products.map((product) => {
          const itemName = `${product.id}-${product.name}`;
          return {
            label: product.isRefunded ? t('modal.order_create_refund.already_refunded', { item: itemName }) : itemName,
            value: String(product.id),
            disabled: product.isRefunded,
          };
        })}
      />
      <div />
      <div />
      <Dropdown
        required={true}
        label={t('modal.order_create_refund.type')}
        value={refundType}
        onSelect={setRefundType}
        items={refundDetail.types.map((type) => ({
          label: type.name || t('modal.order_refund_types.invalid_type'),
          value: String(type.id),
        }))}
      />
      <div />
      <div />
      <Dropdown
        required={true}
        label={t('modal.order_create_refund.include_discount')}
        value={boolToYesNo(hasDiscount)}
        onSelect={(v) => setHasDiscount(yesNoToBool(v))}
        items={yesNoOptions}
      />
      <div />
      <div />
      <Input
        required={true}
        label={t('modal.order_create_refund.memo')}
        placeholder={t('modal.order_create_refund.memoPlaceholder')}
        value={memo}
        onChange={setMemo}
        multiline={true}
        rows={6}
        maxLength={1000}
        InputLabelProps={{
          shrink: true,
          style: {
            whiteSpace: 'break-spaces',
          },
        }}
      />
      <div />
      <div />
      <div className={styles.orderCreateRefundFinal}>
        <div className={clsx([styles.text06rem, styles.textGrey])}>{t('modal.order_create_refund.refund_amount')}</div>
        <div className={styles.text15rem}>
          {formatCurrency({ value: amountToRefund, currency: refundDetail.currency })}
        </div>
      </div>
    </div>
  );

  const canUpdate = submitR.kind !== 'Loading' && selectedItem !== '' && memo.length > 0 && refundType !== '';

  const onSubmit = () => {
    const amountString = amountToRefund.toFixed(refundDetail.currencyDigitDecimals);
    submitR.mutate({
      productId: Number(selectedItem),
      discountFeeIncluded: hasDiscount,
      // Removed shipping fee, authentication fee, and customs fee options YES/NO
      authFeeIncluded: true,
      shippingFeeIncluded: true,
      dutyAndTaxFeeIncluded: true,
      type: Number(refundType),
      comment: memo,
      orderId: Number(order.orderId),
      currency: refundDetail.currency,
      amount: Number(amountString.replace(/,/g, '')),
    });
  };

  return (
    <StandardModal
      title={t('modal.order_create_refund.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={t('common.submit').toUpperCase()}
      submittable={canUpdate}
      body={body}
      containerStyle={{ width: 550 }}
      modalName="order_create_refund"
    />
  );
};

export default View;
